import { Box } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { sendNotifications } from '../../../../apis/sendNotification';
import Button from '../../../../components/Button/Button';
import Modal from '../../../../components/Modal/Modal';
import Toast from '../../../../components/Toast/Toast';
import { LANG_TEXT } from '../../../../constants/language';
import { DamagedItemStatus } from '../../../../interfaces/index.types';
import { IOrders } from '../../order.types';
import { buttonStyle, textButton, useStyles } from '../DrawerOrderItem/styles';

interface ConfirmDamagedProps {
  foodQuantities: Record<string, number>;
  toggleDrawer?: ((open: boolean) => void) | undefined;
  updatePromotionOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateRegularOrderStatus?: ((orderId: string, status: string) => Promise<void>) | undefined;
  updateOrderStatus?: ((orderId: string, status: string, updatedFoods?: IOrders['foods']) => Promise<void>) | undefined;
  order?: IOrders;
  restaurantId?: string;
}

const ConfirmDamaged: React.FC<ConfirmDamagedProps> = ({
  foodQuantities,
  toggleDrawer,
  updatePromotionOrderStatus,
  updateRegularOrderStatus,
  updateOrderStatus,
  order,
  restaurantId,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openModalDamaged, setOpenModalDamaged] = useState<boolean>(false);
  const handleOpenModalDamaged = () => setOpenModalDamaged(true);
  const handleCloseModalDamaged = () => setOpenModalDamaged(false);
  const navigate = useNavigate();
  const toast = useRef<{ type: 'error' | 'success'; content: string }>({
    type: 'success',
    content: '',
  });
  const [openToast, setOpenToast] = useState(false);

  const handleConfirmDamagedOrder = async (order: IOrders) => {
    try {
      const parent_id = order?.parent_id ?? order.id;
      const updatedFoods = order.foods.map((food) => {
        if (restaurantId && food.restaurantId !== restaurantId) {
          return food;
        }
        const newQuantity = foodQuantities[food.item_food_id] ?? food.item_quantity;

        return {
          ...food,
          item_quantity: newQuantity,
          item_subtotal: parseFloat((newQuantity * food.item_price).toFixed(2)),
        };
      });

      const foodsToCalculate = restaurantId ? updatedFoods.filter((food) => food.restaurantId === restaurantId) : updatedFoods;
      const totalQuantity = foodsToCalculate.reduce((sum, food) => sum + food.item_quantity, 0);

      if (totalQuantity === 0) return;

      const newStatus = DamagedItemStatus.DAMAGED_PREPARING;
      if (order.promotion_foods && order.promotion_foods.length > 0) {
        if (order.type === 'promotion') {
          await updatePromotionOrderStatus?.(order.id, newStatus);
        } else {
          await updateRegularOrderStatus?.(order.id, newStatus);
        }
      }
      await updateOrderStatus?.(order.id, newStatus, updatedFoods);

      if (order.rider_id) {
        await sendNotifications(order.rider_id, {
          id: parent_id,
          status: newStatus,
        });
      }
      // addToQueue({
      //   data: order,
      // });
      toggleDrawer!(false);
      toast.current = {
        content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE),
        type: 'success',
      };
      setOpenToast(true);
      // navigate(`/dashboard`);
    } catch (error) {
      toast.current = {
        content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE),
        type: 'error',
      };
      setOpenToast(true);
    }
  };

  return (
    <Box className={classes.BoxButton}>
      <Button
        id="BUTTON_CONFIRM_DAMAGED_ORDER"
        fullWidth={false}
        title={t(LANG_TEXT.CONFIRM_DAMAGED_ORDER)}
        buttonStyle={{
          ...buttonStyle,
          background: 'var(--Primary-50, #F2FBF3) !important',
        }}
        sxTextStyle={{
          ...textButton,
          color: 'var(--Primary-700, #296C32) !important',
        }}
        onClick={() => handleOpenModalDamaged()}
      />
      <Modal
        open={openModalDamaged}
        handleClose={handleCloseModalDamaged}
        handleSubmit={() => handleConfirmDamagedOrder(order!)}
        title={t(LANG_TEXT.CONFIRM_DAMAGED_TITLE)}
        description={t(LANG_TEXT.CONFIRM_DAMAGED_DESCRIPTION)}
      />
      <Toast
        content={toast.current?.content}
        variant={toast.current?.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
    </Box>
  );
};

export default ConfirmDamaged;
